import { strFromU8, unzipSync } from "fflate";
import { parseCsv } from "../features/products/csv";

/**
 * A spreadsheet the person picked, as rows of text: CSV (commas or semicolons) or the
 * first sheet of an Excel file (.xlsx). Read in the browser; nothing is uploaded whole.
 */
export async function readSheet(file: File): Promise<string[][]> {
  if (/\.xlsx$/i.test(file.name)) return readXlsx(await file.arrayBuffer());
  return parseCsv(await file.text());
}

/** "B12" -> 1 (columns from zero). */
function column(ref: string) {
  let n = 0;
  for (const ch of ref.replace(/\d+$/, "")) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** The first sheet of an .xlsx: an Office Open XML zip, read with the zip library we already have. */
function readXlsx(buffer: ArrayBuffer): string[][] {
  const files = unzipSync(new Uint8Array(buffer));
  const xml = (path: string) => {
    const bytes = files[path];
    return bytes ? new DOMParser().parseFromString(strFromU8(bytes), "application/xml") : null;
  };
  const shared = [...(xml("xl/sharedStrings.xml")?.getElementsByTagName("si") ?? [])].map((si) =>
    [...si.getElementsByTagName("t")].map((t) => t.textContent ?? "").join(""),
  );
  const id = xml("xl/workbook.xml")?.getElementsByTagName("sheet")[0]?.getAttribute("r:id");
  const target = [...(xml("xl/_rels/workbook.xml.rels")?.getElementsByTagName("Relationship") ?? [])]
    .find((r) => r.getAttribute("Id") === id)
    ?.getAttribute("Target");
  const path = target ? (target.startsWith("/") ? target.slice(1) : `xl/${target}`) : "xl/worksheets/sheet1.xml";
  const sheet = xml(path);
  if (!sheet) return [];

  const rows: string[][] = [];
  for (const row of sheet.getElementsByTagName("row")) {
    const cells: string[] = [];
    for (const c of row.getElementsByTagName("c")) {
      const at = column(c.getAttribute("r") ?? "");
      const type = c.getAttribute("t");
      const raw = c.getElementsByTagName("v")[0]?.textContent ?? "";
      const value =
        type === "s"
          ? (shared[Number(raw)] ?? "")
          : type === "inlineStr"
            ? [...c.getElementsByTagName("t")].map((t) => t.textContent ?? "").join("")
            : raw;
      cells[at >= 0 ? at : cells.length] = value;
    }
    const filled = Array.from(cells, (v) => v ?? "");
    if (filled.some((v) => v.trim())) rows.push(filled);
  }
  return rows;
}
